import React, { useEffect, useMemo, useState } from "react";
import { Card, Input, Button, Upload, Table, Space, message, Segmented, Switch, Row, Col } from "antd";
import { InboxOutlined, MailOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { TextArea } = Input;

const makeLeadId = () => `LEAD-${Date.now()}`;

const parseEmailText = (text) => {
  const lines = text.split("\n").map((l) => l.trim());
  const findField = (prefix) => {
    const line = lines.find((l) => l.toLowerCase().startsWith(prefix));
    return line ? line.split(":").slice(1).join(":").trim() : "";
  };
  const name = findField("name") || "Email Lead";
  const email =
    findField("email") ||
    (text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/) || [])[0] ||
    "";
  const phone =
    findField("phone") ||
    (text.match(/(\+?\d[\d\s-]{6,})/) || [])[0] ||
    "";
  return { id: makeLeadId(), name, email, phone, source: "email", category: "potential", status: "New" };
};

export default function LeadInbox() {
  const [emailText, setEmailText] = useState("");
  const [staged, setStaged] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState("email");
  const [isDark, setIsDark] = useState(true);
  const useDarkStyles = true;

  useEffect(() => {
    let mounted = true;
    const loadInbox = async () => {
      try {
        const res = await apiFetch(API_CONFIG.leadsInboxListUrl, { method: "POST" });
        const data = await res.json();
        if (mounted && res.ok && Array.isArray(data.inboxLeads) && data.inboxLeads.length) {
          setStaged((prev) => (prev.length ? prev : data.inboxLeads));
        }
      } catch {
        // keep inbox empty if mock data fails to load
      }
    };
    loadInbox();
    return () => {
      mounted = false;
    };
  }, []);

  const addEmailLead = () => {
    if (!emailText.trim()) {
      message.error("Paste an email body first.");
      return;
    }
    const lead = parseEmailText(emailText);
    setStaged((prev) => [lead, ...prev]);
    setEmailText("");
  };

  const uploadProps = {
    beforeUpload: (file) => {
      setUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const leads = rows.map((row) => ({
          id: makeLeadId(),
          name: row.Name || row.name || "Excel Lead",
          email: row.Email || row.email || "",
          phone: row.Phone || row.phone || "",
          source: "excel",
          category: "potential",
          status: "New",
        }));
        setStaged((prev) => [...leads, ...prev]);
        setUploading(false);
        message.success(`Parsed ${leads.length} leads from Excel`);
      };
      reader.onerror = () => {
        setUploading(false);
        message.error("Failed to read Excel file");
      };
      reader.readAsArrayBuffer(file);
      return false;
    },
  };

  const saveStaged = async () => {
    if (!staged.length) return;
    await Promise.all(
      staged.map((lead) =>
        apiFetch(API_CONFIG.leadsSaveUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(lead),
        })
      )
    );
    message.success("Leads saved to system");
    setStaged([]);
  };

  const columns = useMemo(
    () => [
      { title: "Name", dataIndex: "name", key: "name" },
      { title: "Email", dataIndex: "email", key: "email" },
      { title: "Phone", dataIndex: "phone", key: "phone" },
      { title: "Source", dataIndex: "source", key: "source" },
    ],
    []
  );

  return (
    <div
      className={`appverse-content table-container tabled ${useDarkStyles ? "luxe-dark" : "luxe-light"}`}
      style={{
        background: isDark ? "#000" : "#fff",
        minHeight: "100vh",
        marginLeft: "var(--sidenav-width, 88px)",
        padding: "0 20px 0 12px",
        position: "relative",
        zIndex: 1002,
      }}
    >
      <Row gutter={0}>
        <Col xs={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24 luxe-dark-bg"
            style={{ margin: 0, position: "relative", zIndex: 1003 }}
            title={<span className="luxe-card-title">Lead Inbox</span>}
            extra={
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="mode-toggle">
                  <Segmented
                    options={[
                      { label: "Email", value: "email" },
                      { label: "Excel", value: "excel" },
                      { label: "Staged", value: "staged" },
                    ]}
                    value={viewMode}
                    onChange={(v) => setViewMode(v)}
                  />
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 4 }}>
                  <span style={{ color: "#fff8dc", fontWeight: 600 }}>Dark</span>
                  <Switch checked={isDark} onChange={(v) => setIsDark(v)} />
                </div>
              </div>
            }
            bodyStyle={{ background: "#000", borderRadius: "18px" }}
          >
            {viewMode === "email" && (
              <Card
                title="Incoming Email Parser"
                bordered={false}
                className="luxe-dark-bg"
                extra={
                  <Space>
                    <Button className="luxe-btn" icon={<MailOutlined />} onClick={() => setEmailText("Name: Thabo Maseko\nEmail: thabo@example.com\nPhone: +27 72 555 0111\nRequest: Loan for equipment")}>
                      Load Sample Email
                    </Button>
                    <Button className="luxe-btn" type="primary" onClick={addEmailLead}>Parse & Stage Lead</Button>
                  </Space>
                }
              >
                <TextArea rows={8} placeholder="Paste incoming email content here..." value={emailText} onChange={(e) => setEmailText(e.target.value)} />
              </Card>
            )}
            {viewMode === "excel" && (
              <Card
                title="Excel Parsing Simulation"
                bordered={false}
                className="luxe-dark-bg"
                extra={
                  <Upload {...uploadProps} showUploadList={false}>
                    <Button className="luxe-btn" icon={<FileExcelOutlined />} loading={uploading}>
                      Upload Excel
                    </Button>
                  </Upload>
                }
              >
                <p>Upload a spreadsheet with columns: Name, Email, Phone.</p>
              </Card>
            )}
            {viewMode === "staged" && (
              <Card
                title="Staged Leads"
                bordered={false}
                className="luxe-dark-bg"
                extra={
                  <Space>
                    <Button className="luxe-btn" icon={<InboxOutlined />} onClick={saveStaged} disabled={!staged.length}>
                      Save to Leads
                    </Button>
                    <Button className="luxe-btn" onClick={() => setStaged([])} disabled={!staged.length}>
                      Clear
                    </Button>
                  </Space>
                }
              >
                <Table rowKey="id" dataSource={staged} columns={columns} className="luxe-table" />
              </Card>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
